import { Pagination } from '@quero-delivery/quero-components-web';
import { Text } from '@querodelivery/uni-react';
import type { CSSProperties } from 'react';
import { format } from '@querodelivery/qd-date-helper';
import { useNavigate } from 'react-router-dom';
import styles from './styles.module.scss';

import {
  DEFAULT_LIMIT_REQUEST,
  MediaQueryMobile,
} from '../../../../../Constants';
import JsonView from '../../../../../Shared/JsonView';

import type { ListProps } from './types';
import { TableSkeleton } from '@/_components/skeletons/table-skeleton/table-skeleton';
import { Table, useStackedTableLayout } from '@/_components/table';
import type { MotoboyList } from '../../types';
import WhatsappNumber from '@/Components/WhatsappNumber/WhatsappNumber';
import { ENUM_DELIVERYMAN_STATUS } from '@/_constants/enums';
import { normalizeStatus } from '@/Utils/FnUtils';
import { TRANSLATE_DELIVERY_TYPE } from '@/_features/deliveries/constants';
import { TableEmptyState } from '@/_components/table-empty-state/table-empty-state';

const limit = DEFAULT_LIMIT_REQUEST;
const MOTOBOYS_TABLE_GRID_COLUMNS = 'repeat(5, minmax(0, 1fr))';
const WORD_BREAK_STYLE: CSSProperties = {
  overflowWrap: 'break-word',
  wordBreak: 'break-word',
  minWidth: 0,
};

function List({ page, setPage, isLoading, motoboys }: ListProps) {
  const isStackedTableLayout = useStackedTableLayout({
    customQuery: MediaQueryMobile,
  });
  const navigate = useNavigate();

  const checkType = (motoboy: MotoboyList) => {
    const linked = motoboy.isLinked ? TRANSLATE_DELIVERY_TYPE.LINKED : '';
    const cloud = motoboy.isCloud ? TRANSLATE_DELIVERY_TYPE.CLOUD : '';

    return (cloud || linked) + (linked && cloud ? ` + ${linked}` : '') || '-';
  };

  const navigateToMotoboyDetails = (motoboyId: string) => {
    navigate(`/entregadores/${motoboyId}`, {
      state: { from: 'motoboyList' },
    });
  };

  function renderMotoboyRow(motoboy: MotoboyList) {
    const deliverymanTypeLabel = checkType(motoboy);

    return (
      <Table.Row
        key={motoboy._id}
        gridTemplateColumns={MOTOBOYS_TABLE_GRID_COLUMNS}
        onClick={() => {
          navigateToMotoboyDetails(motoboy._id);
        }}
      >
        <Table.Cell className={styles.tcMotoboy}>
          <Text
            fontWeight="semibold"
            tone="supporting"
            style={WORD_BREAK_STYLE}
          >
            {motoboy.nome}
          </Text>
          <Text fontWeight="regular" tone="supporting" style={WORD_BREAK_STYLE}>
            ({motoboy._id})
          </Text>
        </Table.Cell>
        <Table.Cell>
          <WhatsappNumber
            number={
              motoboy.status === ENUM_DELIVERYMAN_STATUS.EXCLUIDO
                ? motoboy.oldCelular || ''
                : motoboy.celular
            }
          />
        </Table.Cell>
        <Table.Cell>
          <Text>
            {motoboy.isDedicated && <span className={styles.tag}>D</span>}
            {normalizeStatus(motoboy.status)}
          </Text>
        </Table.Cell>
        <Table.Cell>
          <Text>
            {motoboy.approvedAt
              ? format(new Date(motoboy.approvedAt), 'dd/MM/yyyy HH:mm')
              : format(new Date(motoboy.createdAt), 'dd/MM/yyyy HH:mm')}
          </Text>
        </Table.Cell>
        <Table.Cell>
          <Text>{deliverymanTypeLabel}</Text>
        </Table.Cell>
      </Table.Row>
    );
  }

  function renderMotoboysMobileCard(motoboy: MotoboyList) {
    const deliverymanTypeLabel = checkType(motoboy);

    return (
      <Table.MobileCard
        key={motoboy._id}
        className={styles.motoboyCard}
        onClick={() => {
          navigateToMotoboyDetails(motoboy._id);
        }}
      >
        <Table.MobileItem label="Nome">
          <div>
            <Text
              fontWeight="semibold"
              tone="supporting"
              style={WORD_BREAK_STYLE}
            >
              {motoboy.nome}
            </Text>
            <Text
              fontWeight="regular"
              tone="supporting"
              style={WORD_BREAK_STYLE}
            >
              ({motoboy._id})
            </Text>
          </div>
        </Table.MobileItem>
        <Table.MobileItem label="Telefone">
          <WhatsappNumber
            number={
              motoboy.status === ENUM_DELIVERYMAN_STATUS.EXCLUIDO
                ? motoboy.oldCelular || ''
                : motoboy.celular
            }
          />
        </Table.MobileItem>
        <Table.MobileItem label="Status">
          {motoboy.isDedicated && <span className={styles.tag}>D</span>}
          {normalizeStatus(motoboy.status)}
        </Table.MobileItem>
        <Table.MobileItem label="Cadastro">
          {motoboy.approvedAt
            ? format(new Date(motoboy.approvedAt), 'dd/MM/yyyy HH:mm')
            : format(new Date(motoboy.createdAt), 'dd/MM/yyyy HH:mm')}
        </Table.MobileItem>
        <Table.MobileItem label="Tipo">{deliverymanTypeLabel}</Table.MobileItem>
      </Table.MobileCard>
    );
  }

  return (
    <div className="mgn-top-sm">
      {isLoading ? (
        <TableSkeleton columnCount={5} rowCount={5} />
      ) : motoboys?.data && motoboys.data.length > 0 ? (
        <>
          <Text
            variant="headingMd"
            tone="supporting"
            className={styles.headerLabel}
          >
            {motoboys?.totalCount || 0}{' '}
            {motoboys?.totalCount === 1 ? 'entregador' : 'entregadores'}
          </Text>
          {isStackedTableLayout ? (
            <Table.MobileList className={styles.motoboysMobileList}>
              {motoboys?.data.map(motoboy => renderMotoboysMobileCard(motoboy))}
            </Table.MobileList>
          ) : (
            <div className={styles.motoboysTableScroll}>
              <Table className={styles.motoboysTable}>
                <Table.Header>
                  <Table.Row gridTemplateColumns={MOTOBOYS_TABLE_GRID_COLUMNS}>
                    <Table.HeaderCell>Nome</Table.HeaderCell>
                    <Table.HeaderCell>Telefone</Table.HeaderCell>
                    <Table.HeaderCell>Status</Table.HeaderCell>
                    <Table.HeaderCell>Cadastro</Table.HeaderCell>
                    <Table.HeaderCell>Tipo</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>

                <Table.Body>
                  {motoboys?.data.map(motoboy => renderMotoboyRow(motoboy))}
                </Table.Body>
              </Table>
            </div>
          )}
          <div className={styles.pagination}>
            <Pagination
              size="sm"
              limitPerPage={limit}
              currentPage={page}
              totalCount={motoboys?.totalCount || 0}
              onPageChange={setPage}
            />
          </div>
        </>
      ) : (
        <TableEmptyState
          title="Nenhum entregador encontrado"
          description="Não encontramos entregadores com os filtros selecionados."
        />
      )}

      <footer className="end-page-margin" />

      <JsonView name="Entregadores" source={motoboys} />
    </div>
  );
}

export { List };
